import { extractScheduleArray, getOpeningHoursFromSchedule } from './scheduleUtils';

const normalizeName = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[®™]/g, '')
    .replace(/[\u2018\u2019\u201B\u2032\u2035\u0060\u00B4']/g, '')
    .replace(/[“”«»"]/g, '')
    .replace(/–/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
};

const ENTITY_TYPE_ALIASES = {
  ATTRACTION: new Set(['ATTRACTION', 'RIDE']),
  SHOW: new Set(['SHOW', 'ENTERTAINMENT']),
  RESTAURANT: new Set(['RESTAURANT', 'DINING', 'FOOD', 'DINING_VENUE', 'RESTAURANT_VENUE'])
};

const canonicalizeEntityType = (value) => {
  const normalizedValue = typeof value === 'string'
    ? value.trim().toUpperCase()
    : '';

  if (!normalizedValue) {
    return 'ATTRACTION';
  }

  const matchedType = Object.entries(ENTITY_TYPE_ALIASES).find(([, aliases]) => aliases.has(normalizedValue));

  return matchedType?.[0] || normalizedValue;
};

const getThemeparksLocationEntries = (apiWaitTimes) => {
  const rootChildren = Array.isArray(apiWaitTimes?.locations?.children)
    ? apiWaitTimes.locations.children
    : [];

  const flattenedEntries = [];
  const queue = [...rootChildren];

  while (queue.length > 0) {
    const currentEntry = queue.shift();

    if (!currentEntry) {
      continue;
    }

    flattenedEntries.push(currentEntry);

    if (Array.isArray(currentEntry.children) && currentEntry.children.length > 0) {
      queue.push(...currentEntry.children);
    }
  }

  return flattenedEntries;
};

export const getThemeparksLiveData = (apiWaitTimes) => {
  const liveData = apiWaitTimes?.waitTimes?.liveData || apiWaitTimes?.liveData;

  if (Array.isArray(liveData)) {
    return liveData;
  }

  if (Array.isArray(apiWaitTimes?.waitTimes)) {
    return apiWaitTimes.waitTimes;
  }

  return [];
};

const buildLocationIndexes = (apiWaitTimes) => {
  const children = getThemeparksLocationEntries(apiWaitTimes);

  const byId = new Map();
  const byExternalId = new Map();
  const byName = new Map();

  children.forEach((item) => {
    if (item?.id) {
      byId.set(item.id, item);
    }

    if (item?.externalId) {
      byExternalId.set(item.externalId, item);
    }

    const normalized = normalizeName(item?.name);
    if (normalized && !byName.has(normalized)) {
      byName.set(normalized, item);
    }
  });

  return { byId, byExternalId, byName };
};

const resolveLocationEntry = (item, locationIndexes) => {
  if (!item || !locationIndexes) {
    return null;
  }

  if (item.id && locationIndexes.byId.has(item.id)) {
    return locationIndexes.byId.get(item.id);
  }

  if (item.externalId && locationIndexes.byExternalId.has(item.externalId)) {
    return locationIndexes.byExternalId.get(item.externalId);
  }

  const normalized = normalizeName(item.name);
  if (normalized && locationIndexes.byName.has(normalized)) {
    return locationIndexes.byName.get(normalized);
  }

  return null;
};

const resolveEntityType = (item, locationEntry) => {
  return canonicalizeEntityType(locationEntry?.entityType || item?.entityType || 'ATTRACTION');
};

const resolveLandLabel = (item, locationEntry) => {
  const candidate =
    (typeof item?.land === 'string' && item.land) ||
    item?.land?.name ||
    item?.land?.label ||
    (typeof item?.area === 'string' && item.area) ||
    item?.area?.name ||
    locationEntry?.parent?.name ||
    null;

  return candidate || null;
};

const normalizeLiveStatus = (item) => {
  const rawStatus = typeof item?.status === 'string'
    ? item.status.trim().toLowerCase()
    : '';

  if (['operating', 'open', 'opened', 'virtualqueue'].includes(rawStatus)) {
    return 'OPERATING';
  }

  if (['down', 'maintenance', 'closedweather', 'closedice'].includes(rawStatus)) {
    return 'DOWN';
  }

  if (['refurbishment', 'renovation', 'rénovation'].includes(rawStatus)) {
    return 'REFURBISHMENT';
  }

  if (['closed', 'ferme', 'fermée'].includes(rawStatus)) {
    return 'CLOSED';
  }

  if (typeof item?.is_open === 'boolean') {
    return item.is_open ? 'OPERATING' : 'CLOSED';
  }

  return item?.status || null;
};

export const normalizeThemeparksEntity = (item, parkConfig, locationIndexes) => {
  const locationEntry = resolveLocationEntry(item, locationIndexes);
  const inferredType = resolveEntityType(item, locationEntry);
  const location = locationEntry?.location || item?.location || null;
  const itemShowtimes = Array.isArray(item?.showtimes)
    ? item.showtimes
    : Array.isArray(locationEntry?.showtimes)
      ? locationEntry.showtimes
      : Array.isArray(locationEntry?.schedule)
        ? locationEntry.schedule
        : [];
  const derivedStatus = normalizeLiveStatus(item);

  return {
    ...item,
    id: item?.id || item?.name,
    name: item?.name || 'Unknown',
    entityType: inferredType,
    parkId: parkConfig?.id,
    parkEntityId: parkConfig?.entityId,
    status: derivedStatus,
    waitTime: item?.waitTime ?? item?.wait_time ?? item?.waitingtime ?? item?.queue?.STANDBY?.waitTime ?? -1,
    singleRiderWaitTime: item?.singleRiderWaitTime ?? item?.queue?.SINGLE_RIDER?.waitTime ?? null,
    land: resolveLandLabel(item, locationEntry),
    showtimes: itemShowtimes,
    location,
    meta: item?.meta || null
  };
};

export const buildThemeparksEntities = (apiWaitTimes, parkConfig, entityTypes = []) => {
  const liveData = getThemeparksLiveData(apiWaitTimes);
  const locationEntries = getThemeparksLocationEntries(apiWaitTimes);
  const locationIndexes = buildLocationIndexes(apiWaitTimes);
  const requestedTypes = (Array.isArray(entityTypes) ? entityTypes : [entityTypes])
    .filter(Boolean)
    .map((type) => canonicalizeEntityType(type));
  const liveEntities = liveData
    .map((item) => normalizeThemeparksEntity(item, parkConfig, locationIndexes))
    .filter((item) => {
      if (!requestedTypes.length) {
        return true;
      }

      return requestedTypes.includes(item.entityType);
    });

  // Include every requested type in the fallback so parks whose /live endpoint returns an empty
  // liveData array (e.g. Mirabilandia on themeparks.wiki) still render attractions from /children.
  // Dedup below keeps live-data entries authoritative when both sources overlap.
  const fallbackTypes = [...requestedTypes];

  if (!fallbackTypes.length) {
    return liveEntities;
  }

  const existingEntityKeys = new Set();

  liveEntities.forEach((item) => {
    if (item?.id) {
      existingEntityKeys.add(`id:${item.id}`);
    }

    if (item?.externalId) {
      existingEntityKeys.add(`external:${item.externalId}`);
    }

    const normalizedName = normalizeName(item?.name);
    if (normalizedName) {
      existingEntityKeys.add(`name:${normalizedName}`);
    }
  });

  const locationFallbackEntities = locationEntries
    .map((item) => normalizeThemeparksEntity(item, parkConfig, locationIndexes))
    .filter((item) => {
      if (!fallbackTypes.includes(item.entityType)) {
        return false;
      }

      const normalizedName = normalizeName(item?.name);
      return !existingEntityKeys.has(`id:${item.id}`)
        && !existingEntityKeys.has(`external:${item.externalId}`)
        && !existingEntityKeys.has(`name:${normalizedName}`);
    });

  return [...liveEntities, ...locationFallbackEntities];
};

export const buildThemeparksMapEntities = (apiWaitTimes, parkConfig) => {
  return buildThemeparksEntities(apiWaitTimes, parkConfig, ['ATTRACTION', 'SHOW', 'RESTAURANT']);
};

export const buildThemeparksOpeningHours = (apiWaitTimes) => {
  const scheduleEntries = extractScheduleArray(apiWaitTimes?.schedule);
  const openingHours = getOpeningHoursFromSchedule(scheduleEntries);
  const aquascopeScheduleEntries = extractScheduleArray(apiWaitTimes?.relatedSchedules?.aquascope);
  const aquascopeOpeningHours = getOpeningHoursFromSchedule(aquascopeScheduleEntries);

  return {
    ...(openingHours || { openingTime: null, closingTime: null, date: null, isFuture: false }),
    scheduleEntries,
    aquascope: aquascopeOpeningHours
      ? {
          ...aquascopeOpeningHours,
          scheduleEntries: aquascopeScheduleEntries
        }
      : null
  };
};

export const getThemeparksFormattedDate = (language) => {
  return new Date().toLocaleDateString(language || undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export const formatThemeparksShowtimes = (showtimes, language) => {
  if (!Array.isArray(showtimes) || showtimes.length === 0) {
    return [];
  }

  const now = Date.now();

  return [...showtimes]
    .map((showtime) => {
      const startValue = showtime?.startTime || showtime?.startDate || showtime;
      const date = typeof startValue === 'string' ? new Date(startValue) : null;

      if (typeof startValue === 'string' && /^\d{1,2}:\d{2}$/.test(startValue)) {
        return {
          label: startValue,
          timestamp: Number.MAX_SAFE_INTEGER,
          isPassed: false
        };
      }

      if (!date || Number.isNaN(date.getTime())) {
        return null;
      }

      return {
        label: date.toLocaleTimeString(language || undefined, {
          hour: '2-digit',
          minute: '2-digit'
        }),
        timestamp: date.getTime(),
        isPassed: date.getTime() < now
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp);
};
